import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Flex, Box, BoxProps } from "../../atoms";
import { styled } from "utils";
import { NextButton, PrevButton } from "./carouselArrow";
import { CarouselDot } from "./carouselDot";

const StyledCarousel = styled(Box, {
  overflow: "hidden",
  position: "relative",
});

const StyledDotButtons = styled(Flex, {
  position: "absolute",
  bottom: "$3",
  width: "100%",
  gap: "$2",
  justifyContent: "center",
});

type Props = BoxProps;

const Carousel: React.FC<Props> = ({ children, ...props }) => {
  const [emblaRef, embla] = useEmblaCarousel();
  const [prevEnabled, setPrevEnabled] = React.useState(false);
  const [nextEnabled, setNextEnabled] = React.useState(false);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const scrollTo = React.useCallback(
    (index: number) => embla && embla.scrollTo(index),
    [embla]
  );
  const scrollPrev = React.useCallback(
    () => embla && embla.scrollPrev(),
    [embla]
  );
  const scrollNext = React.useCallback(
    () => embla && embla.scrollNext(),
    [embla]
  );

  const onSelect = React.useCallback(() => {
    if (embla) {
      setSelectedIndex(embla.selectedScrollSnap());
      setPrevEnabled(embla.canScrollPrev());
      setNextEnabled(embla.canScrollNext());
    }
  }, [embla, setSelectedIndex]);

  React.useEffect(() => {
    if (embla) {
      onSelect();
      setScrollSnaps(embla.scrollSnapList());
      embla.on("select", onSelect);
    }
  }, [embla, onSelect]);

  return (
    <StyledCarousel ref={emblaRef} {...props}>
      <Flex css={{ height: "100%" }}>{children}</Flex>
      <Box css={{ visibility: "hidden", "@bp2": { visibility: "visible" } }}>
        <PrevButton onClick={scrollPrev} disabled={!prevEnabled} />
        <NextButton onClick={scrollNext} disabled={!nextEnabled} />
      </Box>
      <StyledDotButtons>
        {scrollSnaps.map((_, index) => (
          <CarouselDot
            key={index}
            aria-label={`Carousel navigation ${index}`}
            selected={index === selectedIndex}
            onClick={() => scrollTo(index)}
          />
        ))}
      </StyledDotButtons>
    </StyledCarousel>
  );
};

const CarouselItem: React.FC<Props> = (props) => {
  return <Box css={{ position: "relative", flex: "0 0 100%" }} {...props} />;
};

export { Carousel, CarouselItem };
